// App.jsx
import { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { AppProviders } from './context/AppProviders';
import ChatApp from './components/chat/ChatApp';
import Sidebar from './components/sidebar/Sidebar';
import LoginPage from './components/auth/LoginPage';

import {
  REACT_APP_USER_POOL_ID,
  REACT_APP_APP_CLIENT_ID,
  REACT_APP_AWS_REGION,
  REACT_APP_COGNITO_DOMAIN,
  REACT_APP_REDIRECT_SIGN_IN,
  REACT_APP_REDIRECT_SIGN_OUT,
} from './config';

// ─── Amplify configuration ────────────────────────────────────────────────────

const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId:       REACT_APP_USER_POOL_ID    || 'YOUR_USER_POOL_ID',
      userPoolClientId: REACT_APP_APP_CLIENT_ID   || 'YOUR_APP_CLIENT_ID',
      region:           REACT_APP_AWS_REGION      || 'us-east-1',

      loginWith: {
        email:    true,
        username: false,
        phone:    false,

        // OAuth is only injected when a Cognito hosted domain has been configured.
        // See config.js for instructions on setting REACT_APP_COGNITO_DOMAIN.
        ...(REACT_APP_COGNITO_DOMAIN && {
          oauth: {
            domain:          REACT_APP_COGNITO_DOMAIN,
            scopes:          ['email', 'openid', 'profile'],
            redirectSignIn:  [REACT_APP_REDIRECT_SIGN_IN  || 'http://localhost:3000'],
            redirectSignOut: [REACT_APP_REDIRECT_SIGN_OUT || 'http://localhost:3000'],
            responseType:    'code',
          },
        }),
      },

      passwordFormat: {
        minLength:               8,
        requireLowercase:        true,
        requireUppercase:        true,
        requireNumbers:          true,
        requireSpecialCharacters: true,
      },
    },
  },
};

Amplify.configure(amplifyConfig);

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  const [user, setUser]                   = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [oauthError, setOauthError]       = useState('');
  const [isMobileOpen, setIsMobileOpen]   = useState(false);

  // Check whether there is already an authenticated user in the current session.
  const checkCurrentUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch {
      // getCurrentUser throws when there is no authenticated session — this is expected.
      setUser(null);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  useEffect(() => {
    // Parse OAuth errors that Cognito appends as query params on redirect back.
    // e.g. http://localhost:3000/?error=invalid_request&error_description=invalid_scope
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    if (error) {
      const msg = errorDescription
        ? errorDescription.replace(/_/g, ' ')
        : 'Google sign-in failed. Please try again.';
      setOauthError(msg.charAt(0).toUpperCase() + msg.slice(1) + '.');
      // Remove the error params from the URL without a page reload
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Initial check on mount
    checkCurrentUser();

    // Listen to Amplify auth events so we react immediately when the user
    // signs in (including the Google OAuth redirect callback) or signs out.
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkCurrentUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
        case 'signInWithRedirect_failure':
          setOauthError('Google sign-in failed. Please try again.');
          break;
        default:
          break;
      }
    });

    // hubListener is a cleanup function returned by Hub.listen
    return () => hubListener();
  }, []);

  // Minimal dark-background hold while the initial auth check runs.
  // This prevents a flash of the login page on page refresh for signed-in users.
  if (isCheckingAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--theme-bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }} />
    );
  }

  if (!user) {
    return <LoginPage oauthError={oauthError} />;
  }

  return (
    <AppProviders user={user} signOut={amplifySignOut}>
      <div className="app-container">
        <Sidebar isMobileOpen={isMobileOpen} onMobileClose={() => setIsMobileOpen(false)} />
        <ChatApp onMobileBurgerClick={() => setIsMobileOpen(true)} />
      </div>
    </AppProviders>
  );
}

export default App;
